<template>
    <div>
        <app-errors :form="form" class="mb-10"></app-errors>

        <p class="mb-10">Thank you for your subscription in <strong>Mammoth Research, LLC</strong>. Due to securities regulations, we are required to verify your accredited investor status. Please upload any of the following documents to complete the verification:</p>

        <div v-if="form.documents.length < 1">
            <div class="-space-y-px bg-white rounded-md">
                <label class="relative flex p-4 border border-gray-200 cursor-pointer rounded-tl-md rounded-tr-md" :class="{
                    'bg-teal bg-opacity-5 border-teal z-10': form.documentsType == 'Assets'
                }">
                    <input type="radio" v-model="form.documentsType" value="Assets" class="h-4 w-4 mt-0.5 cursor-pointer text-teal border-gray-300 focus:ring-teal">
                    <div class="flex flex-col ml-3 -mt-1">
                        <span class="block text-lg font-medium text-gray-900">
                            Assets
                        </span>
                        <span class="block text-sm text-gray-500">
                            Account statements or third party appraisals dated within the past 90 days proving at least $1M of assets outside of your primary residence. You do not need to prove more than $1M in assets.
                        </span>
                    </div>
                </label>

                <label class="relative flex p-4 border border-gray-200 cursor-pointer" :class="{
                    'bg-teal bg-opacity-5 border-teal z-10': form.documentsType == 'Professional Letter'
                }">
                    <input type="radio" v-model="form.documentsType" value="Professional Letter" class="h-4 w-4 mt-0.5 cursor-pointer text-teal border-gray-300 focus:ring-teal">
                    <div class="flex flex-col ml-3 -mt-1">
                        <span class="block text-lg font-medium text-gray-900">
                            Professional Letter or Professional License
                        </span>
                        <span class="block text-sm text-gray-500">
                            Official written communication dated within the past 90 days from a licensed CPA, attorney, investment advisor or registered broker-dealer that you are an Accredited Investor. If you are licensed as an investment professional, please submit a Broker Check Detailed Report or SEC Investment Advisor Detailed Report. <a :href="asset('/files/ACCREDITED-LETTER-EXAMPLE.pdf')" class="!text-black !font-bold !no-underline" target="_blank">See example here</a>.
                        </span>
                    </div>
                </label>

                <label class="relative flex p-4 border border-gray-200 cursor-pointer rounded-bl-md rounded-br-md" :class="{
                    'bg-teal bg-opacity-5 border-teal z-10': form.documentsType == 'Income'
                }">
                    <input type="radio" v-model="form.documentsType" value="Income" class="h-4 w-4 mt-0.5 cursor-pointer text-teal border-gray-300 focus:ring-teal">
                    <div class="flex flex-col ml-3 -mt-1">
                        <span class="block text-lg font-medium text-gray-900">
                            Income
                        </span>
                        <span class="block text-sm text-gray-500">
                            Tax returns including W2s, 1099s for the past two years and documentation (W2 or financial statement) verifying your year to date income, to show your individual income exceeds $200,000 (or joint income exceeds $300,000) for each year. Note: If your tax return for the previous year is not yet complete, please send your W2s, 1099s and a financial statement for that year.
                        </span>
                    </div>
                </label>
            </div>
        </div>
        <div v-else>
            <div v-if="form.documentsType == 'Assets'">
                <div class="flex flex-col">
                    <span class="block text-lg font-medium text-gray-900">
                        Assets
                    </span>
                    <span class="block text-sm text-gray-500">
                        Account statements or third party appraisals dated within the past 90 days proving at least $1M of assets outside of your primary residence. You do not need to prove more than $1M in assets.
                    </span>
                </div>
            </div>

            <div v-if="form.documentsType == 'Professional Letter'">
                <div class="flex flex-col">
                    <span class="block text-lg font-medium text-gray-900">
                        Professional Letter or Professional License
                    </span>
                    <span class="block text-sm text-gray-500">
                        Official written communication dated within the past 90 days from a licensed CPA, attorney, investment advisor or registered broker-dealer that you are an Accredited Investor. If you are licensed as an investment professional, please submit a Broker Check Detailed Report or SEC Investment Advisor Detailed Report. <a :href="asset('/files/ACCREDITED-LETTER-EXAMPLE.pdf')" class="!text-black !font-bold !no-underline" target="_blank">See example here</a>.
                    </span>
                </div>
            </div>

            <div v-if="form.documentsType == 'Income'">
                <div class="flex flex-col">
                    <span class="block text-lg font-medium text-gray-900">
                        Income
                    </span>
                    <span class="block text-sm text-gray-500">
                        Tax returns including W2s, 1099s for the past two years and documentation (W2 or financial statement) verifying your year to date income, to show your individual income exceeds $200,000 (or joint income exceeds $300,000) for each year. Note: If your tax return for the previous year is not yet complete, please send your W2s, 1099s and a financial statement for that year.
                    </span>
                </div>
            </div>

            <div class="space-y-4 mt-14">
                <app-downloadables :items="form.documents"></app-downloadables>
            </div>
        </div>

        <div v-show="form.documentsType && form.documents.length <= 0" class="flex items-center justify-center mb-8 mt-14">
            <div class="block w-full max-w-lg">
                <label class="block mb-1 text-sm font-medium text-gray-700">Choose a file</label>
                <uploads-field v-model="form.documents" :show-list="form.documents.length < 1" accept=".pdf" desc="Accepts PDF files up to 10MB"></uploads-field>
            </div>
        </div>
    </div>
</template>

<script>
import HeadingTitle from '@Components/HeadingTitle.vue'
import FieldValue from '@Components/FieldValue.vue'
import AppErrors from '@Components/Errors'
import AppDownloadables from '@Components/Downloadables'
import UploadsField from '@Fields/Uploads'
import useForm from '@/form'
import { convertIntakeValue } from '@/utils'
import { watch } from '@vue/runtime-core'

export default {
    components: {
        HeadingTitle,
        FieldValue,
        AppErrors,
        AppDownloadables,
        UploadsField
    },
    props: {
        data: {
            type: Object,
            required: true
        }
    },
    setup(props){
        const form = useForm(_.assign({
            documentsType: '',
            documents: [],
            uploadLater: false
        }, props.data));

        watch(() => form.documents, (documents) => {
            form.post(route('intake.validate.verification')).then(response => {
                if (response.status == 200) {
                    // add action here
                }
            })
        }, {
            deep: true
        })

        function convertValue(value){
            return (value) ? convertIntakeValue(value) : '-'
        }

        return {
            form,
            convertValue,
        }
    }
}
</script>

<style>

</style>
